import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError, Event } from '@angular/router';
import { BehaviorSubject, Subject, Observable, of, race, timer, combineLatest } from 'rxjs';
import { filter, take, map, timeout, catchError, distinctUntilChanged } from 'rxjs/operators';
import { GuideStep, TooltipOverlay } from './guide.model';

export { GuideStep, TooltipOverlay };

@Injectable({
  providedIn: 'root'
})
export class GuideService {
  private readonly DEBUG = true;

  private deviceRestrictionSubject = new Subject<string>();
  public deviceRestriction$ = this.deviceRestrictionSubject.asObservable();

  public steps: GuideStep[] = [];
  public currentIndex = -1;
  public isRecording = false;
  public isRunning = false;
  public navigateEnabled: any = 1;
  public tourVideoLink: string | null = null;

  private overlaySubject = new BehaviorSubject<TooltipOverlay | null>(null);
  public overlay$ = this.overlaySubject.asObservable();

  private recordingFinishedSubject = new Subject<GuideStep[]>();
  public recordingFinished$ = this.recordingFinishedSubject.asObservable();

  private recordedStepCapturedSubject = new Subject<GuideStep>();
  public recordedStepCaptured$ = this.recordedStepCapturedSubject.asObservable();

  private isRecordingSubject = new BehaviorSubject<boolean>(false);
  public isRecording$ = this.isRecordingSubject.asObservable();

  /** Context set by the add-edit component so AppComponent can navigate back after stop. */
  public activeRecordingContext: { route: string; stepIndex: number | null; selectedAction: 'click' | 'none' } | null = null;

  /** Last element captured during recording — persists across navigation. */
  private lastCapturedStepSubject = new BehaviorSubject<GuideStep | null>(null);
  public lastCapturedStep$ = this.lastCapturedStepSubject.asObservable();
  public get lastCapturedStep(): GuideStep | null { return this.lastCapturedStepSubject.value; }
  public clearLastCapturedStep() {
    this.lastCapturedStepSubject.next(null);
    this.recordedSteps = [];
  }

  /** Emits an element that was clicked during recording, waiting for action confirmation. */
  private pendingActionStepSubject = new BehaviorSubject<GuideStep | null>(null);
  public pendingActionStep$ = this.pendingActionStepSubject.asObservable();
  public get pendingActionStep(): GuideStep | null { return this.pendingActionStepSubject.value; }

  /** Called by AppComponent when user confirms action (click/none) for the pending captured step. */
  /** Holds the actual DOM element whose click was intercepted, so we can re-fire it on confirm. */
  private pendingClickTarget: HTMLElement | null = null;
  /** When true, the next click event in handleDocClick will be skipped (it is the re-fired synthetic click). */
  private skipNextClick = false;

  confirmCapturedStep(action: 'click' | 'none') {
    const step = this.pendingActionStepSubject.value;
    const target = this.pendingClickTarget;
    if (!step) return;

    // Clear pending state first
    this.pendingClickTarget = null;
    this.pendingActionStepSubject.next(null);

    if (action === 'none') {
      // "None" — record the step as an explain-only step (no click during playback)
      step.action = 'none';
      step.manual = true;
      step.customfield3 = '0';
      this.recordedSteps.push(step);
      this.ngZone.run(() => {
        this.lastCapturedStepSubject.next({ ...step });
        this.recordedStepCapturedSubject.next({ ...step });
      });
      // Do NOT re-fire the click — the action is intentionally skipped
      return;
    }

    // "Click" — record the step and re-fire the original click
    step.action = 'click';
    step.manual = false;
    step.customfield3 = '1';
    this.recordedSteps.push(step);
    this.ngZone.run(() => {
      this.lastCapturedStepSubject.next({ ...step });
      this.recordedStepCapturedSubject.next({ ...step });
    });

    // Re-fire the original click after Angular has processed the state change
    // Set skipNextClick so handleDocClick ignores this synthetic event
    if (target) {
      setTimeout(() => {
        this.skipNextClick = true;
        target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      }, 50);
    }
  }

  discardPendingStep() {
    this.pendingClickTarget = null;
    this.pendingActionStepSubject.next(null);
  }

  private finishedSubject = new Subject<void>();
  /** Emits once each time the guide completes or is skipped. */
  public finished$ = this.finishedSubject.asObservable();

  private notificationSubject = new Subject<string>();
  public notification$ = this.notificationSubject.asObservable();

  private recordedSteps: GuideStep[] = [];
  /** Read-only snapshot of all steps recorded in the current session. */
  public get allRecordedSteps(): GuideStep[] { return [...this.recordedSteps]; }

  /** Named handlers stored so they can be properly removed if service is ever destroyed. */
  private readonly onResize = () => this.reflow();
  private readonly onScroll = () => this.reflow();

  constructor(
    private router: Router,
    private ngZone: NgZone
  ) {
    this.log('Service Initialized');

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onResize, true);
      window.addEventListener('scroll', this.onScroll, true);
    });

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        this.log('Router: NavigationStart', event.url);
        this.isNavigating = true;
      }
      if (event instanceof NavigationEnd) {
        this.log('Router: NavigationEnd', event.url);
        this.isNavigating = false;
      }
      if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.log('Router: Navigation Cancelled/Error');
        this.isNavigating = false;
      }
    });
  }

  // --- Public API ---

  start(steps: GuideStep[] | string, stepsOrNothing?: GuideStep[], videoLink?: string) {
    const actualSteps = typeof steps === 'string' ? (stepsOrNothing || []) : steps;
    const deviceType = (actualSteps as any)?.deviceType;

    if (deviceType !== undefined && deviceType !== null) {
      const isMobile = window.innerWidth <= 768;
      const devType = Number(deviceType);

      if (devType === 1 && isMobile) {
        this.deviceRestrictionSubject.next('This tour is available only in Desktop/Web view.');
        return;
      }

      if (devType === 2 && !isMobile) {
        this.deviceRestrictionSubject.next('This tour is available only in Mobile view.');
        return;
      }
    }

    if (videoLink) {
      this.tourVideoLink = videoLink;
    }

    if (!actualSteps || actualSteps.length === 0) {
      console.warn('[GuideService] Start called with no steps');
      return;
    }

    // Propagate tour-level video link to steps if they don't have one
    actualSteps.forEach(s => {
      if (!s.videoLink && this.tourVideoLink) {
        s.videoLink = this.tourVideoLink;
      }
    });

    this.steps = actualSteps;
    this.currentIndex = 0;
    this.isRunning = true;

    this.log('Starting tour', `Steps: ${this.steps.length}`);
    this.showCurrentStep();
  }



  async next() {
    this.log('Next clicked', `Current Index: ${this.currentIndex}`);
    const currentStep = this.steps[this.currentIndex];

    if (!currentStep) return;

    // Case-insensitive lookup helper
    const getVal = (obj: any, key: string) => {
      if (!obj) return undefined;
      const lk = key.toLowerCase();
      const foundKey = Object.keys(obj).find(k => k.toLowerCase() === lk);
      return foundKey ? obj[foundKey] : undefined;
    };

    const actionDisabled = getVal(currentStep, 'customfield3') == 0 || getVal(currentStep, 'customfield3') == '0' || getVal(currentStep, 'customfield3') === false || getVal(currentStep, 'customfield3') === 'false';

    if (currentStep.manual || actionDisabled) {
      this.log(actionDisabled ? 'Action disabled by flag (Explain only mode)' : 'Manual step - proceeding without action');
    }
    else if (currentStep.action === 'click' || currentStep.actionType === 'click') {
      const el = this.resolveElement(currentStep);

      if (el) {
        this.log('Executing step click action', this.getEffectiveSelector(currentStep));

        this.overlaySubject.next(null);
        this.clearHighlights(); // Clear highlights immediately before action

        this.ngZone.run(() => {
          el.scrollIntoView({ block: 'center', inline: 'center' });
          el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
        });

        if (currentStep.delayAfterActionMs) {
          await new Promise(r => setTimeout(r, currentStep.delayAfterActionMs));
        } else if (currentStep.action !== 'none') {
          // Default tiny wait to let UI animations/secondary menus settle
          await new Promise(r => setTimeout(r, 450));
        }
      } else {
        console.warn('[GuideService] Element not found for click action', this.getEffectiveSelector(currentStep));
        this.clearHighlights(); // Ensure cleared even if element missing
      }
    }
    else if ((currentStep.fnAction || (currentStep as any).action) && typeof (currentStep as any).action === 'function') {
      const fn = currentStep.fnAction || (currentStep as any).action;
      if (fn) fn();
      this.clearHighlights();
    }
    else if (currentStep.actionType === 'custom' && currentStep.fnAction) {
      currentStep.fnAction();
      this.clearHighlights();
    }

    if (currentStep.waitFor) {
      this.log('Waiting for barrier...', currentStep.waitFor);
      const wa = currentStep.waitFor;
      // Wrap selector in a dummy step object to reuse waitForElement logic
      const dummyStep = { selector: wa.selector } as GuideStep;
      const found = await this.waitForElementLegacy(dummyStep, wa.inOverlay ? 'dialog' : 'page', wa.timeoutMs || 6000);

      if (!found) {
        console.warn('[GuideService] Wait barrier failed, proceeding anyway.');
      } else {
        await new Promise(r => setTimeout(r, 450));
      }
    }

    this.currentIndex++;
    if (this.currentIndex < this.steps.length) {
      this.showCurrentStep();
    } else {
      this.log('Tour finished (end of steps)');
      this.finish();
    }
  }

  prev() {
    this.log('Prev clicked', `Current Index: ${this.currentIndex}`);
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.overlaySubject.next(null); // Force clear to ensure UI refresh
      this.targetHoveredSubject.next(false); // Reset hover state
      this.setOverlayHovered(false);

      this.showCurrentStep();
    }
  }

  skip() {
    this.log('User skipped tour');
    this.finish();
  }

  finish() {
    this.log('Finishing tour');
    this.isRunning = false;
    this.currentIndex = -1;
    this.tourVideoLink = null;
    this.overlaySubject.next(null);
    this.clearHighlights();
    this.finishedSubject.next();
  }

  // --- Core Logic ---

  private async showCurrentStep() {
    const step = this.steps[this.currentIndex];
    if (!step) {
      this.finish();
      return;
    }

    this.log('Showing step', `${this.currentIndex + 1}/${this.steps.length}`, step);

    // Case-insensitive lookup for customfield3
    const getVal = (obj: any, key: string) => {
      if (!obj) return undefined;
      const lk = key.toLowerCase();
      const foundKey = Object.keys(obj).find(k => k.toLowerCase() === lk);
      return foundKey ? obj[foundKey] : undefined;
    };

    const navOverride = getVal(step, 'customfield3');
    // Navigate to route only when customfield3 === "1" (navigate dropdown = "Yes").
    // null / undefined / "0" / false all mean "stay on current page".
    const navigationEnabled = navOverride == 1 || navOverride == '1' || navOverride === true || navOverride === 'true';

    this.log(`[Navigation Check] Step: ${this.currentIndex + 1}, Route: ${step.route}, Current: ${this.router.url}, customfield3: ${navOverride}, NavigationEnabled: ${navigationEnabled}`);

    if (step.route && navigationEnabled && !this.isCurrentRoute(step.route)) {
      this.log(`[Navigation] customfield3=1 — routing to ${step.route}`);
      await this.navigateAndShow(step);
    } else {
      this.log(`[Navigation] Staying on current page. ${!navigationEnabled ? '(customfield3 != 1 — navigation not requested)' : '(Already on route)'}`);
      this.startStepSequence(step);
    }
  }

  private async navigateAndShow(step: GuideStep) {
    this.log('Navigation required', `To: ${step.route}`);
    this.overlaySubject.next(null);

    const navEndPromise = this.router.events.pipe(
      filter(event => 
        event instanceof NavigationEnd || 
        event instanceof NavigationError || 
        event instanceof NavigationCancel
      ),
      take(1)
    ).toPromise();

    try {
      await this.router.navigateByUrl(step.route!);
      const result = await navEndPromise;
      
      if (result instanceof NavigationError || result instanceof NavigationCancel) {
        this.log('Navigation failed or was cancelled', result);
        this.finish(); // Stop tour on failure
        this.notificationSubject.next('Navigation failed. Tour stopped.');
        return;
      }
      
      this.log('Navigation completed', 'Waiting for DOM...');
      // Give a small buffer for layout stability after navigation
      await new Promise(r => setTimeout(r, 600)); 
      this.startStepSequence(step);
    } catch (err) {
      this.log('Navigation exception', err);
      this.finish();
    }
  }

  // --- Resolution & Waiting ---

  // --- Resolution & Waiting ---

  // --- Resolution & Waiting ---

  // --- Registry ---
  // --- Registry ---
  private registry = new Map<string, HTMLElement>();
  private registration$ = new Subject<{ key: string, el: HTMLElement }>();

  public normalizeRoute(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  register(key: string, el: HTMLElement) {
    this.registry.set(key, el);
    this.registration$.next({ key, el });
  }

  unregister(key: string) {
    this.registry.delete(key);
  }

  public waitFor(predicate: (key: string) => boolean, timeoutMs: number = 3000): Observable<HTMLElement | null> {
    return new Observable<HTMLElement | null>(observer => {
      // 1. Setup subscription to catch FUTURE events
      // We subscribe FIRST to ensure no events are missed in the tiny gap before map check.
      const subscription = this.registration$.pipe(
        filter(e => predicate(e.key)),
        take(1)
      ).subscribe(e => {
        observer.next(e.el);
        observer.complete();
      });

      // 2. Check Registry for EXISTING events (Synchronous)
      // If found, emit and teardown subscription immediately.
      for (const [key, el] of this.registry.entries()) {
        if (predicate(key)) {
          if (el.isConnected) {
            subscription.unsubscribe(); // Stop listening
            observer.next(el);
            observer.complete();
            return;
          } else {
            this.registry.delete(key);
          }
        }
      }

      // 3. Return teardown logic
      return () => subscription.unsubscribe();
    }).pipe(
      timeout(timeoutMs),
      catchError(() => of(null))
    );
  }

  private getEffectiveSelector(step: GuideStep): string | null {
    if (step.guideKey) return step.guideKey;
    if (step.route && step.guideId) {
      return `${this.normalizeRoute(step.route)}::${step.guideId}`;
    }
    if (step.guideId) return step.guideId;
    return step.selector || null;
  }

  private startStepSequence(step: GuideStep) {
    const startTs = Date.now();
    const selector = this.getEffectiveSelector(step);
    const context = step.overlayContext || 'page';

    // 0. OPTIMIZATION: Try to resolve IMMEDIATELY before setting up any listeners
    const immediateEl = this.resolveElement(step);
    if (immediateEl) {
      this.log(`[TOUR] Resolved immediately (pre-wait check)`);
      this.highlightAndShow(immediateEl, step);
      return;
    }

    // 1. Try Key-based wait (Preferred, Instant/Event-driven)
    let keyToWaitFor: string | null = null;
    if (step.guideKey) keyToWaitFor = step.guideKey;
    else if (step.route && step.guideId) keyToWaitFor = `${this.normalizeRoute(step.route)}::${step.guideId}`;

    if (keyToWaitFor || step.guideId) {
      this.log(`[TOUR] Waiting for key or suffix: ${keyToWaitFor || step.guideId}`);

      // Predicate for Registry (Directive-based)
      const predicate = (k: string) => {
        if (keyToWaitFor && k === keyToWaitFor) return true;
        if (step.guideId && k.endsWith(`::${step.guideId}`)) return true;
        return false;
      };

      // 1. Registry Wait (For proper directives)
      const registryWait$ = this.waitFor(predicate, 4000);

      // 2. DOM Polling (For [attr.guide] fallback where Directive doesn't run)
      // Uses setInterval at 150ms instead of requestAnimationFrame to avoid blocking the main thread
      const domWait$ = new Observable<HTMLElement>(observer => {
        if (!step.guideId) { observer.complete(); return; }

        const selectorAttr = `[guide="${step.guideId}"]`;
        const timer = setInterval(() => {
          const el = document.querySelector(selectorAttr) as HTMLElement;
          if (el) {
            clearInterval(timer);
            observer.next(el);
            observer.complete();
          }
        }, 150);

        // Auto-cancel after 4s
        setTimeout(() => { clearInterval(timer); observer.complete(); }, 4000);

        return () => clearInterval(timer);
      });

      // 3. Race them! First to find wins.
      race(registryWait$, domWait$).pipe(
        take(1),
        timeout(4500),
        catchError(() => of(null))
      ).subscribe(el => {
        if (el) {
          this.log(`[TOUR] Resolved (Race Winner) in ${Date.now() - startTs}ms`);
          this.highlightAndShow(el, step);
        } else {
          this.log(`[TOUR] Wait timed out. Final fallback...`);
          const fallback = this.resolveElement(step);
          this.highlightAndShow(fallback, step);
        }
      });
      return;
    }

    // 2. Legacy Selector Flow (Polling/Timeout)
    if (!selector) {
      this.highlightAndShow(null, step);
      return;
    }

    this.waitForElementLegacy(step, context, 6000).then(found => {
      if (found) {
        const el = this.resolveElement(step);
        this.highlightAndShow(el, step);
      } else {
        console.warn(`[GuideService] Target not found: ${selector}`);
        this.highlightAndShow(null, step);
      }
    });
  }

  // Renamed old waitForElement to legacy specific
  private async waitForElementLegacy(step: GuideStep, context: string, timeoutMs: number): Promise<boolean> {
    const legacySelector = step.selector;
    if (!legacySelector) return false;

    // Use setInterval at 150ms instead of requestAnimationFrame to avoid blocking the main thread
    return new Promise(resolve => {
      const timer = setInterval(() => {
        if (this.resolveElement(step)) {
          clearInterval(timer);
          resolve(true);
        }
      }, 150);

      setTimeout(() => {
        clearInterval(timer);
        resolve(false);
      }, Math.min(timeoutMs, 3000)); // cap at 3s max
    });
  }

  private resolveElement(step: GuideStep): HTMLElement | null {
    // Priority A: Explicit guideKey from Registry
    if (step.guideKey) {
      const el = this.registry.get(step.guideKey);
      if (el && el.isConnected) return el;
    }

    // Priority B: Computed Route + GuideId from Registry
    if (step.route && step.guideId) {
      const key = `${this.normalizeRoute(step.route)}::${step.guideId}`;
      const el = this.registry.get(key);
      if (el && el.isConnected) return el;
    }

    // Priority C: Simple GuideId (Scan registry keys suffix)
    if (step.guideId) {
      // Direct Registry Scan (Fast)
      for (const [key, el] of this.registry.entries()) {
        if (key.endsWith(`::${step.guideId}`) && el.isConnected) {
          return el;
        }
      }
      // DOM Fallback
      const domEl = document.querySelector(`[guide="${step.guideId}"]`) as HTMLElement;
      if (domEl) return domEl;
    }

    // Priority D: Fallback Selector (Legacy)
    if (step.selector) {
      let root: ParentNode = document;
      if (step.overlayContext === 'dialog') {
        const containers = document.querySelectorAll('.cdk-overlay-container');
        if (containers.length > 0) root = containers[0];
      }
      try {
        const all = root.querySelectorAll(step.selector);
        const idx = step.matchIndex || 0;
        if (all.length > 0 && idx < all.length) {
          return all[idx] as HTMLElement;
        }
      } catch (e) { }
    }

    return null;
  }

  private resolveRoot(step: GuideStep): ParentNode {
    return document; // Legacy helper no longer primarily used, kept if needed
  }

  // --- Hover Detection State ---
  private targetHoveredSubject = new BehaviorSubject<boolean>(false);
  private overlayHoveredSubject = new BehaviorSubject<boolean>(false);

  public showSidePanel$ = combineLatest([
    this.targetHoveredSubject,
    this.overlayHoveredSubject
  ]).pipe(
    map(([target, overlay]) => target || overlay),
    distinctUntilChanged()
  );

  public targetHovered$ = this.targetHoveredSubject.asObservable(); // Keep for legacy if needed
  private currentHoverCleanup: (() => void) | null = null;

  public setOverlayHovered(isHovered: boolean) {
    this.overlayHoveredSubject.next(isHovered);
  }

  private highlightAndShow(el: HTMLElement | null, step: GuideStep) {
    this.clearHighlights();

    // Cleanup previous hover listeners
    if (this.currentHoverCleanup) {
      this.currentHoverCleanup();
      this.currentHoverCleanup = null;
    }
    this.targetHoveredSubject.next(false);
    this.overlayHoveredSubject.next(false); // Reset overlay state too

    // 1. Show highlight immediately
    if (el) {
      el.classList.add('guide-highlight');
      // Use 'auto' to skip the smooth scroll delay unless specified otherwise
      el.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });

      // Setup Hover Listeners if needed
      if (step.rightPanelBullets || step.rightPanelTitle) {
        const onEnter = () => this.ngZone.run(() => this.targetHoveredSubject.next(true));
        const onLeave = () => this.ngZone.run(() => this.targetHoveredSubject.next(false));

        el.addEventListener('mouseenter', onEnter);
        el.addEventListener('mouseleave', onLeave);

        this.currentHoverCleanup = () => {
          el.removeEventListener('mouseenter', onEnter);
          el.removeEventListener('mouseleave', onLeave);
        };
      }
    }

    // 2. Calculate position once — do not repeat on rAF to prevent visible jumps
    this.recalculatePosition();
  }

  // --- Formatting ---
  private formatBullets(bullets: string[] = []): string {
    return bullets.map(b => `• ${b}`).join('\n');
  }

  private clearHighlights() {
    document.querySelectorAll('.guide-highlight').forEach(e => e.classList.remove('guide-highlight'));
  }

  // --- Positioning ---

  public reflow() {
    if (this.isRunning && this.currentIndex >= 0 && this.overlaySubject.value) {
      this.recalculatePosition();
    }
  }

  public reposition(width: number, height: number) {
    this.recalculatePosition({ width, height });
  }

  public recalculatePosition(tooltipSize?: { width: number, height: number }) {
    if (!this.isRunning || this.currentIndex < 0) return;

    const step = this.steps[this.currentIndex];
    const selector = this.getEffectiveSelector(step);

    if (!selector) {
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const tooltipW = tooltipSize?.width || 320;
      const tooltipH = tooltipSize?.height || 180;

      const top = (viewportH / 2) - (tooltipH / 2);
      const left = (viewportW / 2) - (tooltipW / 2);

      this.ngZone.run(() => {
        this.overlaySubject.next({
          step,
          targetRect: { top, left, width: 0, height: 0, bottom: top, right: left, x: left, y: top, toJSON: () => { } },
          position: 'center',
          top,
          left
        });
      });
      return;
    }

    const target = this.resolveElement(step);

    if (!target) {
      // If target not found but we are still running, show in center as fallback
      // especially useful when navigation is skipped via customfield3
      if (!this.overlaySubject.value || (this.overlaySubject.value && this.overlaySubject.value.position !== 'center')) {
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;
        const tooltipW = tooltipSize?.width || 320;
        const tooltipH = tooltipSize?.height || 220;

        const top = (viewportH / 2) - (tooltipH / 2);
        const left = (viewportW / 2) - (tooltipW / 2);

        this.ngZone.run(() => {
          this.overlaySubject.next({
            step,
            targetRect: { top, left, width: 0, height: 0, bottom: top, right: left, x: left, y: top, toJSON: () => { } },
            position: 'center',
            top,
            left
          });
        });
      }
      return;
    }

    const rect = target.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const tooltipW = tooltipSize?.width || 340;
    const tooltipH = tooltipSize?.height || 180; // Conservative height estimate — keeps position stable on first render
    const margin = 12;

    let pos = step.position || 'auto';

    // Space Checks
    const spaceTop = rect.top;
    const spaceBottom = viewportH - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewportW - rect.right;

    // Intelligent Auto-Positioning
    if (pos === 'auto') {
      // Prefer Right > Bottom > Top > Left
      if (spaceRight >= tooltipW + margin) pos = 'right';
      else if (spaceBottom >= tooltipH + margin) pos = 'bottom';
      else if (spaceTop >= tooltipH + margin) pos = 'top';
      else if (spaceLeft >= tooltipW + margin) pos = 'left';
      else pos = 'bottom'; // Fallback
    }

    let top = 0;
    let left = 0;

    // Calculate Tooltip Coords
    switch (pos) {
      case 'top':
        top = rect.top - tooltipH - margin;
        left = rect.left + (rect.width / 2) - (tooltipW / 2);
        break;
      case 'bottom':
        top = rect.bottom + margin;
        left = rect.left + (rect.width / 2) - (tooltipW / 2);
        break;
      case 'left':
        top = rect.top + (rect.height / 2) - (tooltipH / 2);
        left = rect.left - tooltipW - margin;
        break;
      case 'right':
        top = rect.top + (rect.height / 2) - (tooltipH / 2);
        left = rect.right + margin;
        break;
      // Center fallback logic?
    }

    // Viewport Constraint (Clamping)
    const padding = 15;
    if (left < padding) left = padding;
    if (left + tooltipW > viewportW - padding) left = viewportW - tooltipW - padding;

    if (top < padding) top = padding;
    if (top + tooltipH > viewportH - padding) top = viewportH - tooltipH - padding;

    // Arrow Calculation (Relative to Tooltip)
    let arrowLeft = 0;
    let arrowTop = 0;
    const arrowSize = 10;

    if (pos === 'top' || pos === 'bottom') {
      // Arrow moves horizontally
      const targetCenterX = rect.left + (rect.width / 2);
      arrowLeft = targetCenterX - left - arrowSize;
      // Clamp Arrow
      arrowLeft = Math.max(10, Math.min(tooltipW - 20 - arrowSize, arrowLeft));
    } else {
      // Arrow moves vertically
      const targetCenterY = rect.top + (rect.height / 2);
      arrowTop = targetCenterY - top - arrowSize;
      // Clamp Arrow
      arrowTop = Math.max(10, Math.min(tooltipH - 20 - arrowSize, arrowTop));
    }

    this.ngZone.run(() => {
      this.overlaySubject.next({
        step,
        targetRect: rect,
        position: pos as string,
        top,
        left,
        arrowPosition: {
          left: arrowLeft,
          top: arrowTop
        }
      });
    });
  }

  private isCurrentRoute(route: string): boolean {
    if (!route) return true;
    
    // Cleanup routes for comparison (remove trailing slashes, normalize leading slash)
    const normalize = (u: string) => {
      let cleaned = u.split('?')[0].split('#')[0].replace(/\/+$/, '');
      if (cleaned && !cleaned.startsWith('/')) cleaned = '/' + cleaned;
      return cleaned || '/';
    };

    const current = normalize(this.router.url);
    const target = normalize(route);

    return current === target;
  }

  public log(message: string, ...args: any[]) {
    if (this.DEBUG) {
      console.log(`%c[GuideService] ${message}`, 'color: #3f51b5; font-weight: bold;', ...args);
    }
  }

  // --- Recorder Logic ---

  startRecording(navigateEnabled?: any) {
    if (this.isRecording) return;
    this.navigateEnabled = navigateEnabled !== undefined ? navigateEnabled : 1;
    this.isRecording = true;
    this.isRecordingSubject.next(true);
    this.recordedSteps = [];
    this.lastCapturedStepSubject.next(null);
    this.pendingActionStepSubject.next(null);
    this.pendingClickTarget = null;
    document.addEventListener('click', this.handleDocClick, true);
    this.log('Recording started', `Navigate enabled: ${this.navigateEnabled}`);
  }

  stopRecording() {
    this.isRecording = false;
    this.isRecordingSubject.next(false);
    this.pendingActionStepSubject.next(null);
    this.pendingClickTarget = null;
    document.removeEventListener('click', this.handleDocClick, true);
    
    // Use setTimeout to allow Angular zone to pick up isRecording=false
    // and update UI (like the record button icon) BEFORE a synchronous prompt blocks the thread.
    setTimeout(() => {
      this.recordingFinishedSubject.next([...this.recordedSteps]);
    });
    
    return this.recordedSteps;
  }

  private lastRecordedFingerprint: string | null = null;
  private lastRecordedTime = 0;
  private isNavigating = false;

  private handleDocClick = (event: MouseEvent) => {
    if (!this.isRecording) return;
    if (this.isNavigating) return;

    const t = event.target as HTMLElement;
    // Ignore clicks on recorder UI and tour controls
    if (t.closest('.guide-overlay') || t.closest('.recorder-ui') || t.closest('.tour-controls')
      || t.closest('.rec-action-chooser') || t.closest('.global-recording-pill')) return;

    // If there is already a pending capture waiting for confirmation, ignore new clicks
    if (this.pendingActionStepSubject.value) return;

    // Skip re-fired synthetic clicks (dispatched after user confirms "Click")
    if (this.skipNextClick) {
      this.skipNextClick = false;
      return;
    }

    // Always block the original click — we will re-fire it manually if user confirms "Click"
    event.preventDefault();
    event.stopImmediatePropagation();

    const targetEl = this.pickTargetElement(t);
    const tag = targetEl.tagName.toLowerCase();

    // Capture identifiers
    const guideId = targetEl.getAttribute('guide');
    const guideKey = targetEl.getAttribute('data-guide-key');

    // Fallback info
    const stable = this.buildStableSelector(targetEl);

    // If no robust ID and no stable selector, warn
    // Now only warns for buttons, inputs, etc.
    if (!guideId && !guideKey && !stable) {
      const isInteractive = ['button', 'a', 'input', 'select', 'textarea'].includes(tag) ||
        targetEl.hasAttribute('role') || targetEl.closest('.clickable');
      if (isInteractive) {
        this.notificationSubject.next('Weak element. Add [guide]="id" to element.');
      }
      return;
    }

    const isDialog = !!targetEl.closest('.mat-mdc-dialog-container, .mat-dialog-container, .cdk-overlay-pane');

    // Determine action
    let actionType: 'click' | 'none' = 'click';
    if (tag === 'input' || tag === 'textarea' || tag === 'select') actionType = 'none';

    const currentRoute = this.router.url.split('?')[0].split('#')[0];

    const newStep: GuideStep = {
      id: `step_${this.recordedSteps.length + 1}`,
      text: 'Describe step action...',
      route: currentRoute,
      action: actionType,
      overlayContext: isDialog ? 'dialog' : 'page',
      manual: actionType === 'none',

      // Ids
      guideId: guideId || undefined,
      guideKey: guideKey || undefined,

      // Fallback
      selector: stable?.selector || (guideId ? `[guide="${guideId}"]` : targetEl.tagName),
      matchIndex: stable?.matchIndex || 0,

      description: 'Auto-recorded step',
      customfield3: this.navigateEnabled == 1 ? '1' : '0'
    };

    // cleanup empty
    if (!newStep.guideId) delete newStep.guideId;
    if (!newStep.guideKey) delete newStep.guideKey;

    this.log('Recorded Step (pending action confirmation)', newStep);

    // Add wait logic if navigating/opening dialogs (heuristic)
    if (newStep.action === 'click') {
      const countSelectors = '.mat-mdc-dialog-container, .mat-dialog-container';
      const preCount = document.querySelectorAll(countSelectors).length;
      setTimeout(() => {
        const postCount = document.querySelectorAll(countSelectors).length;
        if (postCount > preCount) {
          newStep.waitFor = {
            selector: countSelectors,
            inOverlay: true,
            timeoutMs: 6000
          };
        }
      }, 800);
    }

    // Emit for action chooser — do NOT push to recordedSteps yet
    // Store the real DOM element so we can re-fire the click if user confirms
    this.pendingClickTarget = targetEl;
    this.ngZone.run(() => this.pendingActionStepSubject.next({ ...newStep }));
  };

  private pickTargetElement(el: HTMLElement): HTMLElement {
    const guided = el.closest('[guide]') as HTMLElement;
    if (guided) return guided;

    if (['mat-icon', 'svg', 'path', 'span', 'div'].includes(el.tagName.toLowerCase())) {
      const interactive = el.closest('button, a, [role="button"], [routerLink], .calsoft-vertical-navigation-item') as HTMLElement;
      if (interactive) return interactive;
    }

    return el.closest('button, a, input, select, textarea, [role="button"]') as HTMLElement || el;
  }

  private buildStableSelector(el: HTMLElement, root: Element | Document = document): { selector: string, matchIndex: number } | null {
    let sel = '';
    const guide = el.getAttribute('guide');
    if (guide) sel = `[guide="${guide}"]`;
    else if (el.id && !el.id.startsWith('mat-')) sel = `#${el.id}`;
    else {
      const attrs = ['routerLink', 'name', 'formControlName'];
      for (const a of attrs) {
        const val = el.getAttribute(a);
        if (val) { sel = `${el.tagName.toLowerCase()}[${a}*="${val}"]`; break; }
      }
    }

    if (!sel || ['div', 'span', 'mat-icon'].includes(sel.toLowerCase())) return null;

    const matches = root.querySelectorAll(sel);
    let idx = 0;
    for (let i = 0; i < matches.length; i++) { if (matches[i] === el) { idx = i; break; } }
    return { selector: sel, matchIndex: idx };
  }

  exportGuideJson() { return JSON.stringify(this.recordedSteps, null, 2); }
  importGuideJson(json: string) {
    try {
      const data = JSON.parse(json);
      if (data.stepGuideSteps) {
        const steps = data.stepGuideSteps;
        if (steps) {
          (steps as any).deviceType = data.deviceType;
        }
        this.start(steps, [], data.videoLink);
      } else {
        this.start(data);
      }
    }
    catch (e) { console.error('[GuideService] Invalid JSON'); }
  }
}
